import { useUserContext } from "../../Hooks/useUserContext";
import { useUnitEquivalentService } from "../../Hooks/useUnitEquivalentService";
import { RouterLink } from "../../Shared/RouterLink";
import React, { useState, useEffect } from "react";
import {
	Grid,
	Typography,
	TextField,
	Button,
	Paper,
	Snackbar,
	SnackbarContent
} from "@material-ui/core";
import NotLoggedInIcon from "@material-ui/icons/MoodBad";
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";
import ConfirmIcon from "@material-ui/icons/CheckCircle";
import { AutoResizeTextarea } from "../../Shared/AutoResizeTextarea";
import green from "@material-ui/core/colors/green";

export function EditUnitEquivalent(props) {
	const user = useUserContext(props.config);
	const service = useUnitEquivalentService(props.config);
	const isCreateMode = props.match.params.unitEquivalentId === undefined;
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [toastOpen, setToastOpen] = useState(false);
	const [item, setItem] = useState({
		unitEquivalentId: 0,
		preferredUnit: "",
		aliases: "",
		forceDecimalPlacesRounding: null,
		notes: ""
	});

	const hasPermission = user?.isUnitAdmin ?? false;

	const normalizeAliasesForEditor = (aliases) => {
		if (!aliases) return "";
		if (Array.isArray(aliases)) {
			return aliases
				.map((alias) => (alias ?? "").trim())
				.filter((alias) => alias)
				.join("\n");
		}
		return aliases
			.split(/\||\r?\n/)
			.map((alias) => alias.trim())
			.filter((alias) => alias)
			.join("\n");
	};

	const toAliasesArray = (aliasesText) => {
		if (!aliasesText) return [];
		return aliasesText
			.split(/\||\r?\n/)
			.map((alias) => alias.trim())
			.filter((alias) => alias);
	};

	useEffect(() => {
		const title = isCreateMode ? "Create Unit Equivalent" : "Edit Unit Equivalent";
		document.title = `${title} - ${props.config.appName}`;

		if (!hasPermission)
			return;

		if (isCreateMode) {
			setIsLoading(false);
		} else {
			const id = parseInt(props.match.params.unitEquivalentId);
			service.getById(id, (response) => {
				if (response && response.status === 200 && response.data) {
					setItem({
						...response.data,
						aliases: normalizeAliasesForEditor(response.data.aliases)
					});
				}
				setIsLoading(false);
			}, (error) => {
				console.error(error);
				if (error.response && error.response.status === 404) {
					props.history.push("/notfound");
				} else {
					alert("Failed to load unit equivalent");
				}
				setIsLoading(false);
			});
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	function handleChange(event) {
		const { name, value } = event.target;
		setItem({
			...item,
			[name]: value
		});
	}

	function handleSave() {
		setIsSaving(true);

		const saveFunction = isCreateMode ? service.create : service.update;
		const payload = {
			UnitEquivalentId: item.unitEquivalentId,
			PreferredUnit: item.preferredUnit,
			Aliases: toAliasesArray(item.aliases),
			ForceDecimalPlacesRounding: item.forceDecimalPlacesRounding,
			Notes: item.notes
		};
		const saveArgs = isCreateMode ? [payload] : [item.unitEquivalentId, payload];

		saveFunction(...saveArgs, (response) => {
			if (response && response.status === 200) {
				setToastOpen(true);
				setTimeout(() => {
					props.history.push("/admin/unit-equivalents");
				}, 1000);
			} else {
				alert("Failed to save unit equivalent");
			}
			setIsSaving(false);
		}, (error) => {
			console.error(error);
			alert("Failed to save unit equivalent: " + (error.response?.data || error.message));
			setIsSaving(false);
		});
	}

	if (!hasPermission)
		return (
			<Grid container spacing={8}>
				<Grid item xs={12}>
					<NotLoggedInIcon fontSize="large" />
					<Typography variant="subtitle1">
						Sorry, you must {user.isLoggedIn ? "" : "be logged in and "}have unit admin permissions to view this page!
					</Typography>
				</Grid>
			</Grid>
		);

	if (isLoading)
		return <Typography style={{ padding: 20 }}>Loading...</Typography>;

	return (
		<Grid container spacing={3} style={{ padding: 20 }}>
			<Grid item xs={12}>
				<Typography variant="h4" gutterBottom>
					{isCreateMode ? "Create Unit Equivalent" : "Edit Unit Equivalent"}
				</Typography>
			</Grid>
			<Grid item xs={12}>
				<Paper style={{ padding: 20 }}>
					<Grid container spacing={3}>
						{/* Preferred Unit */}
						<Grid item xs={12} container alignItems="center" spacing={2}>
							<Grid item xs={12} md={3} lg={2}>
								<Typography>Preferred Unit:</Typography>
							</Grid>
							<Grid item xs={12} md={9} lg={10}>
								<TextField
									fullWidth
									name="preferredUnit"
									value={item.preferredUnit}
									onChange={handleChange}
									required
									helperText="The canonical unit name (e.g., 'g', 'tsp', 'cups')"
								/>
							</Grid>
						</Grid>
						{/* Aliases */}
						<Grid item xs={12} container alignItems="flex-start" spacing={2}>
							<Grid item xs={12} md={3} lg={2} style={{ paddingTop: 16 }}>
								<Typography>Aliases:</Typography>
							</Grid>
							<Grid item xs={12} md={9} lg={10}>
								<TextField
									fullWidth
									name="aliases"
									value={item.aliases}
									onChange={handleChange}
									multiline
									rows={1}
									InputProps={{
										inputComponent: AutoResizeTextarea
									}}
									helperText="Line-separated list of alternative names"
								/>
							</Grid>
						</Grid>
						{/* Decimal Places Rounding */}
						<Grid item xs={12} container alignItems="center" spacing={2}>
							<Grid item xs={12} md={3} lg={2}>
								<Typography>Decimal Places:</Typography>
							</Grid>
							<Grid item xs={12} md={9} lg={10}>
								<TextField
									fullWidth
									name="forceDecimalPlacesRounding"
									value={item.forceDecimalPlacesRounding === null || item.forceDecimalPlacesRounding === undefined ? "" : item.forceDecimalPlacesRounding}
									onChange={(e) => {
										const val = e.target.value;
										setItem({
											...item,
											forceDecimalPlacesRounding: val === "" ? null : parseInt(val, 10)
										});
									}}
									placeholder="(allow fraction)"
									type="number"
									helperText="Null means allow fractions; otherwise number of decimal places to round to"
								/>
							</Grid>
						</Grid>
		{/* Notes */}
		<Grid item xs={12} container alignItems="flex-start" spacing={2}>
			<Grid item xs={12} md={3} lg={2} style={{ paddingTop: 16 }}>
				<Typography>Notes:</Typography>
			</Grid>
			<Grid item xs={12} md={9} lg={10}>
				<TextField
					fullWidth
					name="notes"
					value={item.notes}
					onChange={handleChange}
					multiline
					rows={1}
					InputProps={{
						inputComponent: AutoResizeTextarea
					}}
				/>
			</Grid>
		</Grid>
						<Grid item xs={12} style={{ marginTop: 16 }}>
							<Button
								variant="contained"
								color="primary"
								onClick={handleSave}
								disabled={isSaving}
								style={{ marginRight: 10 }}
							>
								<SaveIcon style={{ marginRight: 10 }} />
								{isSaving ? "Saving..." : "Save"}
							</Button>
							<Button
								variant="outlined"
								component={RouterLink}
								to="/admin/unit-equivalents"
								disabled={isSaving}
							>
								<CancelIcon style={{ marginRight: 10 }} />
								Cancel
							</Button>
						</Grid>
					</Grid>
				</Paper>
			</Grid>
			<Snackbar
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				open={toastOpen}
				autoHideDuration={3000}
				onClose={() => setToastOpen(false)}
				style={{ marginTop: 20 }}
			>
				<SnackbarContent
					style={{ backgroundColor: green[600] }}
					message={
						<div style={{ display: "flex", alignItems: "center" }}>
							<ConfirmIcon />
							<span style={{ paddingLeft: 10 }}>Unit equivalent saved successfully!</span>
						</div>
					}
				/>
			</Snackbar>
		</Grid>
	);
}
